import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardHeader,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Visibility as ViewsIcon,
  TouchApp as ClicksIcon,
  Timeline as PerformanceIcon,
  Refresh as RefreshIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';
import { AdStrategyResponse, AdVariant, trackVariantPerformance, getDebugVariants } from '../../services/adStrategistService';

interface OptimizationTabProps {
  strategy: AdStrategyResponse | null;
}

interface PerformanceData {
  variantId: number;
  format: string;
  title: string;
  target_platform?: string;
  impressions: number;
  clicks: number;
  ctr: number;
  isHighPerforming: boolean;
}

const OptimizationTab: React.FC<OptimizationTabProps> = ({ strategy }) => {
  const [performanceData, setPerformanceData] = useState<PerformanceData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  
  useEffect(() => {
    if (strategy) {
      console.log("Strategy in OptimizationTab:", strategy);
      console.log("Ad variants:", strategy.ad_variants);
      fetchVariantIds();
    }
  }, [strategy]);

  // Fetch actual variant IDs from database before generating mock data
  const fetchVariantIds = async () => {
    if (!strategy) return;
    
    setIsLoading(true);
    try {
      const data = await getDebugVariants(strategy.id);
      console.log("Debug variants data:", data);
      
      if (data.variants && data.variants.length > 0) {
        // Generate mock data using actual database IDs
        generateMockPerformanceData(data.variants);
      } else {
        // Fallback to mock generation without real IDs
        generateMockPerformanceData([]);
      }
    } catch (error) {
      console.error("Error fetching variant IDs:", error);
      // Fallback to mock generation without real IDs
      generateMockPerformanceData([]);
    }
  };
  
  // This function simulates loading performance data that would normally come from the API
  const generateMockPerformanceData = (realVariants: any[] = []) => {
    if (!strategy) return;
    
    // Create a map of real variant IDs by format and platform
    const realVariantMap = new Map();
    realVariants.forEach(v => {
      const key = `${v.format}-${v.target_platform || 'general'}`;
      realVariantMap.set(key, v.id);
    });
    
    console.log("Real variant ID map:", Object.fromEntries(realVariantMap));
    
    const data: PerformanceData[] = strategy.ad_variants.map((variant, index) => {
      // First try to use the variant's own ID if it has one (should be populated by backend)
      let variantId = variant.id;
      
      // If no ID on the variant object, try to get real ID from map by format/platform
      if (!variantId || variantId <= 0) {
        const key = `${variant.format}-${variant.target_platform || 'general'}`;
        variantId = realVariantMap.get(key);
      }
      
      // Last resort - use index+1 as fallback (never use 0 as ID)
      if (!variantId || variantId <= 0) {
        variantId = index + 1;
      }
      
      console.log(`Variant ${variant.format} (platform: ${variant.target_platform || 'general'}): using ID ${variantId}`);
      
      // Simulate different performance metrics for different variants
      const baseImpressions = Math.floor(Math.random() * 500) + 100;
      const baseClicks = Math.floor(Math.random() * 50) + 5;
      const ctr = (baseClicks / baseImpressions) * 100;
      
      return {
        variantId: variantId,
        format: variant.format,
        target_platform: variant.target_platform,
        title: variant.title || `${variant.format} (ID: ${variantId})`,
        impressions: baseImpressions,
        clicks: baseClicks,
        ctr: parseFloat(ctr.toFixed(2)),
        isHighPerforming: ctr > 5 // Consider high-performing if CTR > 5%
      };
    });
    
    console.log("Generated performance data:", data);
    setPerformanceData(data);
    setIsLoading(false);
  };
  
  const handleRefreshData = () => {
    setIsRefreshing(true);
    fetchVariantIds();
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1500);
  };
  
  const handleSimulateImpression = async (variantId: number) => {
    if (!variantId || variantId <= 0) {
      console.error("Cannot track impressions for invalid variant ID:", variantId);
      return;
    }
    
    try {
      console.log(`Tracking impression for variant ID: ${variantId}`);
      await trackVariantPerformance(variantId, 1, 0);
      
      // Update local state to reflect the new impression
      setPerformanceData(prevData => 
        prevData.map(item => 
          item.variantId === variantId 
            ? { 
                ...item, 
                impressions: item.impressions + 1,
                ctr: parseFloat(((item.clicks / (item.impressions + 1)) * 100).toFixed(2))
              } 
            : item
        )
      );
    } catch (error) {
      console.error(`Error recording impression for variant ID ${variantId}:`, error);
      alert(`Failed to record impression. The variant ID ${variantId} may not exist in the database.`);
    }
  };
  
  const handleSimulateClick = async (variantId: number) => {
    if (!variantId || variantId <= 0) {
      console.error("Cannot track clicks for invalid variant ID:", variantId);
      return;
    }
    
    try {
      console.log(`Tracking click for variant ID: ${variantId}`);
      await trackVariantPerformance(variantId, 0, 1);
      
      // Update local state to reflect the new click
      setPerformanceData(prevData => 
        prevData.map(item => 
          item.variantId === variantId 
            ? { 
                ...item, 
                clicks: item.clicks + 1,
                ctr: parseFloat(((item.clicks + 1) / item.impressions * 100).toFixed(2))
              } 
            : item
        )
      );
    } catch (error) {
      console.error(`Error recording click for variant ID ${variantId}:`, error);
      alert(`Failed to record click. The variant ID ${variantId} may not exist in the database.`);
    }
  };
  
  const handleDebugVariants = async () => {
    if (!strategy) return;
    
    try {
      const data = await getDebugVariants(strategy.id);
      setDebugInfo(data);
      
      // Log detailed debug information
      console.log("Strategy variants from frontend:", strategy.ad_variants);
      console.log("Database variants from backend:", data.variants);
      
      // Create a detailed ID mapping for debugging
      const idMapping = {};
      data.variants.forEach((v: any) => {
        const key = `${v.format}-${v.target_platform || 'general'}`;
        idMapping[key] = v.id;
      });
      console.log("Variant ID mapping:", idMapping);
      
      // Compare frontend and backend variants
      strategy.ad_variants.forEach((frontendVariant, index) => {
        const key = `${frontendVariant.format}-${frontendVariant.target_platform || 'general'}`;
        const backendId = idMapping[key];
        const frontendId = frontendVariant.id;
        console.log(`Variant ${index}: Format=${frontendVariant.format}, Frontend ID=${frontendId}, Backend ID=${backendId}`);
      });
      
      // Update the performance data with the actual IDs from the database
      if (data.variants && data.variants.length > 0) {
        const idMap = new Map();
        data.variants.forEach((v: any) => {
          idMap.set(`${v.format}-${v.target_platform || 'general'}`, v.id);
        });
        
        setPerformanceData(prevData => 
          prevData.map(item => {
            const key = `${item.format}-${item.target_platform || 'general'}`;
            const actualId = idMap.get(key);
            if (actualId !== undefined && actualId > 0) {
              console.log(`Updating variant ID: ${item.variantId} to ${actualId}`);
              return {
                ...item,
                variantId: actualId
              };
            }
            return item;
          })
        );
      }
    } catch (error) {
      console.error("Error fetching debug variants:", error);
    }
  };
  
  const formatName = (format: string): string => {
    return format.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  if (!strategy) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', p: 5 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6">Loading strategy...</Typography>
      </Box>
    );
  }
  
  const getTotalPerformance = () => {
    const totals = performanceData.reduce(
      (acc, curr) => {
        acc.impressions += curr.impressions;
        acc.clicks += curr.clicks;
        return acc;
      },
      { impressions: 0, clicks: 0 }
    );
    
    return {
      ...totals,
      ctr: totals.impressions > 0 
        ? parseFloat(((totals.clicks / totals.impressions) * 100).toFixed(2))
        : 0
    };
  };
  
  const totalPerformance = getTotalPerformance();
  
  const getBestPerformer = () => {
    if (performanceData.length === 0) return null;
    
    return performanceData.reduce((best, current) => 
      current.ctr > best.ctr ? current : best
    );
  };
  
  const bestPerformer = getBestPerformer();
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Performance Optimization</Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={handleRefreshData}
            disabled={isRefreshing}
            sx={{ mr: 1 }}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
          </Button>
          <Button 
            variant="outlined" 
            color="secondary"
            onClick={handleDebugVariants}
          >
            Debug Variants
          </Button>
        </Box>
      </Box>
      
      {debugInfo && (
        <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'info.light', color: 'white' }}>
          <Typography variant="subtitle1">Debug Information</Typography>
          <Typography variant="body2">Strategy ID: {debugInfo.strategy_id}</Typography>
          <Typography variant="body2">Variants Count: {debugInfo.variants_count}</Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2">Variant IDs in Database:</Typography>
            {debugInfo.variants.map((v: any) => (
              <Typography key={v.id} variant="body2" sx={{ ml: 2 }}>
                • ID: {v.id}, Format: {v.format}, Platform: {v.target_platform || 'general'}, 
                  Impressions: {v.impressions}, Clicks: {v.clicks}
              </Typography>
            ))}
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2">Frontend vs Backend Variants:</Typography>
            {strategy.ad_variants.map((frontendVariant: any, index: number) => {
              const key = `${frontendVariant.format}-${frontendVariant.target_platform || 'general'}`;
              const matchingBackendVariant = debugInfo.variants.find(
                (v: any) => v.format === frontendVariant.format && 
                (v.target_platform || 'general') === (frontendVariant.target_platform || 'general')
              );
              
              return (
                <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                  • Frontend: Format={frontendVariant.format}, ID={frontendVariant.id || 'undefined'} | 
                  Backend: ID={matchingBackendVariant?.id || 'not found'}
                </Typography>
              );
            })}
          </Box>
        </Paper>
      )}
      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardHeader 
                  title="Total Impressions" 
                  avatar={<ViewsIcon color="primary" />}
                  titleTypographyProps={{ variant: 'h6' }}
                />
                <CardContent>
                  <Typography variant="h3" align="center" color="primary">
                    {totalPerformance.impressions.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Card>
                <CardHeader 
                  title="Total Clicks" 
                  avatar={<ClicksIcon color="secondary" />}
                  titleTypographyProps={{ variant: 'h6' }}
                />
                <CardContent>
                  <Typography variant="h3" align="center" color="secondary">
                    {totalPerformance.clicks.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Card>
                <CardHeader 
                  title="Average CTR" 
                  avatar={<PerformanceIcon color="info" />}
                  titleTypographyProps={{ variant: 'h6' }}
                />
                <CardContent>
                  <Typography variant="h3" align="center" color="info.main">
                    {totalPerformance.ctr}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {bestPerformer && (
            <Paper elevation={1} sx={{ p: 3, mb: 4, bgcolor: 'success.light' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={8}>
                  <Typography variant="h6" sx={{ color: 'white', display: 'flex', alignItems: 'center' }}>
                    <StarIcon sx={{ mr: 1 }} /> 
                    Top Performing Ad
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', mt: 1 }}>
                    "{bestPerformer.title}" is your best performing ad with a CTR of {bestPerformer.ctr}%. 
                    This {formatName(bestPerformer.format)} variant has received {bestPerformer.impressions} impressions 
                    and {bestPerformer.clicks} clicks.
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
                  <Box sx={{ 
                    display: 'inline-flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    bgcolor: 'white', 
                    p: 2, 
                    borderRadius: 2 
                  }}>
                    <Typography variant="h4" color="success.main">
                      {bestPerformer.ctr}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Click-Through Rate
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}
          
          <Paper elevation={1}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Ad Variant</TableCell>
                    <TableCell>Format</TableCell>
                    <TableCell align="right">Impressions</TableCell>
                    <TableCell align="right">Clicks</TableCell>
                    <TableCell align="right">CTR</TableCell>
                    <TableCell align="right">Performance</TableCell>
                    <TableCell align="center">Test Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {performanceData.map((row) => (
                    <TableRow key={row.variantId}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {row.isHighPerforming && (
                            <StarIcon sx={{ color: 'warning.main', mr: 1 }} fontSize="small" />
                          )}
                          {row.title} <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>(ID: {row.variantId})</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={formatName(row.format)} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell align="right">{row.impressions}</TableCell>
                      <TableCell align="right">{row.clicks}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          {row.ctr > 5 ? (
                            <TrendingUpIcon sx={{ color: 'success.main', mr: 0.5 }} fontSize="small" />
                          ) : (
                            <TrendingDownIcon sx={{ color: 'error.main', mr: 0.5 }} fontSize="small" />
                          )}
                          {row.ctr}%
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ width: '100%', mr: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min(row.ctr * 10, 100)}
                            color={row.ctr > 5 ? "success" : row.ctr > 2 ? "warning" : "error"}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box>
                          <Tooltip title="Simulate Impression">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleSimulateImpression(row.variantId)}
                            >
                              <ViewsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Simulate Click">
                            <IconButton 
                              size="small" 
                              color="secondary"
                              onClick={() => handleSimulateClick(row.variantId)}
                            >
                              <ClicksIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              For demonstration purposes, you can simulate impressions and clicks using the test actions.
              In a production environment, these metrics would be collected from actual ad interactions.
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default OptimizationTab; 